import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MerchantCreatedReturnLabelChargeDefault } from "../merchant-created-return-label-charge-default";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

const STORE_CREDIT_PERMISSION = "credit";

export const merchantCreatedReturnsSettingsShape = {
  merchantCreatedReturns: z.object({
    enableMerchantReturnFlow: z.boolean(),
    labelChargeDefault: createEnum(MerchantCreatedReturnLabelChargeDefault),
    claimsRequireShipping: z.boolean(),
  }),
  /** Requires MANAGE_TEAM_ROLES; omitted when the caller lacks it. */
  staffPermissions: z
    .object({
      allowRefund: z.boolean(),
      allowStoreCredit: z.boolean(),
      allowExchange: z.boolean(),
      allowEditFeesAndBonuses: z.boolean(),
      allowEditGreenReturns: z.boolean(),
      allowEditReturn: z.boolean(),
    })
    .optional(),
};

export type MerchantCreatedReturnsSettings = z.infer<
  z.ZodObject<typeof merchantCreatedReturnsSettingsShape>
>;

export function merchantCreatedReturnsSettingsFromTeam({
  settings,
}: ReturnsOperationsSettingsSource): Required<MerchantCreatedReturnsSettings> {
  const { returns } = settings;
  const permissions = returns?.permissions;
  const allowedMethods = permissions?.allowedCompensationMethods;
  return {
    merchantCreatedReturns: {
      enableMerchantReturnFlow: returns?.enableMerchantReturnFlow ?? false,
      labelChargeDefault:
        returns?.merchantCreatedReturnLabelChargeDefault ??
        MerchantCreatedReturnLabelChargeDefault.AUTO,
      claimsRequireShipping:
        returns?.merchantCreatedClaimsRequireShipping ?? false,
    },
    staffPermissions: {
      allowRefund: allowedMethods?.includes(CompensationMethod.REFUND) ?? true,
      allowStoreCredit:
        allowedMethods?.includes(STORE_CREDIT_PERMISSION) ?? true,
      allowExchange:
        allowedMethods?.includes(CompensationMethod.EXCHANGE) ?? true,
      allowEditFeesAndBonuses: permissions?.allowEditFeesAndBonuses ?? true,
      allowEditGreenReturns: permissions?.allowEditGreenReturns ?? true,
      allowEditReturn: permissions?.allowEditReturn ?? true,
    },
  };
}

export function merchantCreatedReturnsTeamPatch(
  next: MerchantCreatedReturnsSettings,
): OperationsSettingsPatch {
  const set: Record<string, unknown> = {
    "returns.enableMerchantReturnFlow":
      next.merchantCreatedReturns.enableMerchantReturnFlow,
    "returns.merchantCreatedReturnLabelChargeDefault":
      next.merchantCreatedReturns.labelChargeDefault,
    "returns.merchantCreatedClaimsRequireShipping":
      next.merchantCreatedReturns.claimsRequireShipping,
  };
  const { staffPermissions } = next;
  if (!staffPermissions) {
    return { set, unset: [] };
  }
  const allowed: string[] = [];
  if (staffPermissions.allowRefund) {
    allowed.push(CompensationMethod.REFUND);
  }
  if (staffPermissions.allowStoreCredit) {
    allowed.push(STORE_CREDIT_PERMISSION);
  }
  if (staffPermissions.allowExchange) {
    allowed.push(CompensationMethod.EXCHANGE);
  }
  set["returns.permissions.allowedCompensationMethods"] = allowed;
  set["returns.permissions.allowEditFeesAndBonuses"] =
    staffPermissions.allowEditFeesAndBonuses;
  set["returns.permissions.allowEditGreenReturns"] =
    staffPermissions.allowEditGreenReturns;
  set["returns.permissions.allowEditReturn"] = staffPermissions.allowEditReturn;
  return { set, unset: [] };
}
